import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Calendar, Bell, LogOut, Menu, X,
  ClipboardList, Target, Settings, Clock, StickyNote, Plus, Trash2,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import logoMarkSrc from '../assets/logo-mark.png';

function RailTooltip({ tooltip }) {
  if (!tooltip) return null;
  return createPortal(
    <div className="portal-rail-tooltip"
         style={{ top: tooltip.top, left: tooltip.left }}>
      {tooltip.text}
    </div>,
    document.body
  );
}

export default function Layout({ children, user, organization, onLogout, unreadNotesCount = 0 }) {
  const location   = useLocation();
  const navigate   = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  const showTooltip = useCallback((e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      text,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  }, []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  const { canViewLeads } = usePermissions();

  /* ── Sticky notes (logic unchanged) ─────────────────────────── */
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dashboard_notes') || '[]'); }
    catch { return []; }
  });
  const [noteInput, setNoteInput] = useState('');

  useEffect(() => {
    localStorage.setItem('dashboard_notes', JSON.stringify(notes));
  }, [notes]);

  const addNote = () => {
    const text = noteInput.trim();
    if (!text) return;
    setNotes(prev => [{ id: Date.now(), text, createdAt: new Date().toISOString() }, ...prev]);
    setNoteInput('');
  };
  const deleteNote = (id) => setNotes(prev => prev.filter(n => n.id !== id));

  /* ── Nav items ───────────────────────────────────────────────── */
  const navItems = [
    { path: '/dashboard',       icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/clients',         icon: Users,           label: 'Clients'   },
    { path: '/client-services', icon: ClipboardList,   label: 'Services'  },
    ...(canViewLeads() ? [{ path: '/leads', icon: Target, label: 'Leads' }] : []),
    { path: '/reminders',       icon: Bell,            label: 'Reminders' },
    { path: '/calendar',        icon: Calendar,        label: 'Calendar'  },
    { path: '/timesheet',       icon: Clock,           label: 'Timesheet' },
  ];

  /* User initials */
  const initials = (user?.name || 'U')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  return (
    <div data-portal="true" className="portal-root">

      {/* ═══ Icon-only Navigation Rail ══════════════════════════ */}
      <nav className="portal-rail hidden md:flex">

        <div className="portal-rail-logo">
          <img src={logoMarkSrc} alt="DueDate" className="portal-rail-logo-img" />
        </div>

        <div className="portal-rail-nav">
          {navItems.map(({ path, icon: Icon, label }) => {
            const active = isActive(path);
            const badge  = label === 'Services' && unreadNotesCount > 0 ? unreadNotesCount : 0;
            return (
              <button key={path}
                      onClick={() => navigate(path)}
                      onMouseEnter={(e) => showTooltip(e, label)}
                      onMouseLeave={hideTooltip}
                      data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                      className={`portal-rail-item${active ? ' active' : ''}`}>
                <Icon size={20} strokeWidth={1.75} />
                {badge > 0 && (
                  <span className="portal-rail-badge">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="portal-rail-bottom">
          <Link to="/settings"
                onMouseEnter={(e) => showTooltip(e, 'Settings')}
                onMouseLeave={hideTooltip}
                className={`portal-rail-item${isActive('/settings') ? ' active' : ''}`}>
            <Settings size={20} strokeWidth={1.75} />
          </Link>
          <button onClick={onLogout}
                  onMouseEnter={(e) => showTooltip(e, 'Sign out')}
                  onMouseLeave={hideTooltip}
                  data-testid="logout-button"
                  className="portal-rail-item portal-rail-item--logout">
            <LogOut size={18} strokeWidth={1.75} />
          </button>
          <div className="portal-rail-avatar"
               onMouseEnter={(e) => showTooltip(e, user?.name)}
               onMouseLeave={hideTooltip}>
            {initials}
          </div>
        </div>
      </nav>

      {/* ═══ MOBILE HEADER ═══════════════════════════════════════ */}
      <div className="portal-mobile-header md:hidden">
        <div className="flex items-center gap-2.5">
          <img src={logoMarkSrc} alt="DueDate" style={{ height: '28px', width: '28px', objectFit: 'contain' }} />
          <span className="font-semibold text-white text-sm tracking-tight">DueDate</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white/70 hover:text-white p-1.5 transition-colors">
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* ═══ MOBILE MENU ═════════════════════════════════════════ */}
      {mobileMenuOpen && (
        <div className="portal-mobile-menu md:hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{organization?.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{user?.name} · <span className="capitalize">{user?.role}</span></p>
          </div>
          <div className="py-1">
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link key={path} to={path} onClick={() => setMobileMenuOpen(false)}
                    className={`portal-mobile-nav-item${isActive(path) ? ' active' : ''}`}>
                <Icon size={17} />
                <span>{label}</span>
              </Link>
            ))}
          </div>
          <div className="p-4 border-t border-gray-100">
            <button onClick={onLogout}
                    className="flex items-center gap-3 text-red-600 text-sm font-medium hover:text-red-700">
              <LogOut size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}

      {/* ═══ Main Workspace Canvas ═══════════════════════════════ */}
      <main className="portal-main pt-14 md:pt-0">
        <div className="portal-content">
          {children}
        </div>
      </main>

      {/* ═══ STICKY NOTES BUBBLE (unchanged) ════════════════════ */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

        {showNotes && (
          <div className="w-80 bg-white rounded-2xl flex flex-col overflow-hidden"
               style={{ maxHeight: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)' }}>

            {/* Header */}
            <div className="relative overflow-hidden px-4 pt-4 pb-3"
                 style={{ background: 'linear-gradient(135deg, #1E293B 0%, #3730A3 100%)' }}>
              <svg className="absolute right-0 top-0 opacity-90" width="110" height="74" viewBox="0 0 110 74" fill="none">
                <rect x="56" y="10" width="42" height="54" rx="4" fill="#000" opacity="0.2"/>
                <rect x="53" y="7" width="42" height="54" rx="4" fill="#FEF9C3" stroke="#FDE047" strokeWidth="1"/>
                <rect x="53" y="7" width="42" height="11" rx="4" fill="#FDE047"/>
                <rect x="53" y="14" width="42" height="4" fill="#FDE047"/>
                <circle cx="60" cy="12" r="2.5" fill="white" opacity="0.9"/>
                <circle cx="69" cy="12" r="2.5" fill="white" opacity="0.9"/>
                <circle cx="78" cy="12" r="2.5" fill="white" opacity="0.9"/>
                <circle cx="87" cy="12" r="2.5" fill="white" opacity="0.9"/>
                <line x1="59" y1="28" x2="89" y2="28" stroke="#FDE047" strokeWidth="1.2"/>
                <line x1="59" y1="36" x2="89" y2="36" stroke="#FDE047" strokeWidth="1.2"/>
                <line x1="59" y1="44" x2="89" y2="44" stroke="#FDE047" strokeWidth="1.2"/>
                <line x1="59" y1="52" x2="80" y2="52" stroke="#FDE047" strokeWidth="1.2"/>
                <g transform="translate(98,62) rotate(-50)">
                  <rect x="-4" y="-28" width="8" height="36" rx="1.5" fill="#FBBF24"/>
                  <rect x="-4" y="-28" width="8" height="7" rx="1.5" fill="#FDA4AF"/>
                  <rect x="-4" y="-21" width="8" height="3" fill="#D1D5DB"/>
                  <polygon points="-4,8 4,8 0,18" fill="#F97316"/>
                  <polygon points="-2,14 2,14 0,18" fill="#292524"/>
                </g>
                <path d="M8 20 L9.8 25 L15 27 L9.8 29 L8 34 L6.2 29 L1 27 L6.2 25 Z" fill="#FBBF24" opacity="0.9"/>
                <path d="M38 8 L39 10.5 L41.5 11.5 L39 12.5 L38 15 L37 12.5 L34.5 11.5 L37 10.5 Z" fill="#A5F3FC" opacity="0.8"/>
                <circle cx="22" cy="52" r="3" fill="#A78BFA" opacity="0.7"/>
                <circle cx="32" cy="62" r="2" fill="#34D399" opacity="0.6"/>
                <circle cx="14" cy="40" r="1.5" fill="#F472B6" opacity="0.7"/>
              </svg>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-white tracking-tight">Quick Notes</span>
                    {notes.length > 0 && (
                      <span className="text-[11px] bg-amber-400/25 text-amber-300 font-bold px-2 py-0.5 rounded-full">
                        {notes.length}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">Jot anything. It stays here.</p>
                </div>
                <button onClick={() => setShowNotes(false)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-slate-500 hover:text-white transition-colors">
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Input */}
            <div className="px-3 pt-3 pb-2 border-b border-gray-100 bg-amber-50/60">
              <div className="flex gap-2">
                <textarea
                  className="flex-1 text-sm resize-none border border-amber-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent bg-white placeholder-gray-400 leading-snug"
                  rows={2} placeholder="What's on your mind?"
                  value={noteInput} onChange={e => setNoteInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                />
                <button onClick={addNote} disabled={!noteInput.trim()}
                        className="self-end w-8 h-8 rounded-xl flex items-center justify-center text-white transition-all disabled:opacity-35 disabled:cursor-not-allowed flex-shrink-0 shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #1E293B, #3730A3)' }}>
                  <Plus size={16} />
                </button>
              </div>
              <p className="text-[10px] text-amber-500/70 mt-1.5 ml-1 font-medium">↵ Enter to save · Shift+Enter for new line</p>
            </div>

            {/* Notes list */}
            <div className="overflow-y-auto flex-1 p-3 space-y-2.5" style={{ background: '#FAFAF9' }}>
              {notes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <p className="text-sm font-semibold text-gray-700">Nothing jotted yet</p>
                  <p className="text-xs text-gray-400 mt-0.5">Your thoughts, tasks & reminders live here</p>
                </div>
              ) : notes.map((note, idx) => {
                const palettes = [
                  { bg: '#FEFCE8', border: '#FDE047', fold: '#FCD34D' },
                  { bg: '#FFF7ED', border: '#FED7AA', fold: '#FDBA74' },
                  { bg: '#F0FDF4', border: '#BBF7D0', fold: '#86EFAC' },
                  { bg: '#EFF6FF', border: '#BFDBFE', fold: '#93C5FD' },
                ];
                const p = palettes[idx % palettes.length];
                return (
                  <div key={note.id}
                       className="group relative rounded-xl px-3 pt-2.5 pb-3 text-sm text-gray-800 leading-snug flex items-start gap-2"
                       style={{ background: p.bg, border: `1px solid ${p.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                    <div className="absolute bottom-0 right-0 rounded-bl-sm"
                         style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '0 0 16px 16px',
                                  borderColor: `transparent transparent ${p.fold} transparent` }} />
                    <span className="flex-1 whitespace-pre-wrap break-words pr-1">{note.text}</span>
                    <button onClick={() => deleteNote(note.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-0.5 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 mt-0.5">
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bubble button */}
        <button onClick={() => setShowNotes(v => !v)}
                className="relative w-14 h-14 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #1E293B 0%, #3730A3 100%)',
                         boxShadow: '0 8px 24px rgba(55,48,163,0.5)' }}
                title="Quick Notes">
          {notes.length > 0 && !showNotes && (
            <span className="absolute inset-0 rounded-full animate-ping"
                  style={{ background: '#F59E0B', opacity: 0.25 }} />
          )}
          <StickyNote size={22} className={showNotes ? 'text-amber-400' : 'text-white'} />
          {notes.length > 0 && !showNotes && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white shadow-sm"
                  style={{ background: '#F59E0B' }}>
              {notes.length > 9 ? '9+' : notes.length}
            </span>
          )}
        </button>

      </div>

      <RailTooltip tooltip={tooltip} />
    </div>
  );
}
